import React, { useEffect, useRef, useState, useMemo } from 'react';
import { EditorialLogo } from 'components/MenuBar/GlobalHeader';
import { supabase } from 'lib/supabaseClient';

/**
 * LandingPageRedesign - "Documentary Editorial" Aesthetic
 *
 * Design Philosophy:
 * - Warm, human, authentic - not cold tech startup
 * - Editorial magazine layout with intentional asymmetry
 * - Earth tones with optimistic accents
 * - "Rising from communities" visual motif
 * - Paper-like textures that feel real, not digital
 */

// ============================================
// SHARED STYLES
// ============================================
const SharedStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,400;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,600&family=Source+Sans+3:wght@400;500;600&display=swap');

    .landing-redesign {
      --color-cream: #FAF7F2;
      --color-warm-white: #FFFEF9;
      --color-charcoal: #1C1917;
      --color-charcoal-soft: #44403C;
      --color-charcoal-muted: #78716C;
      --color-terracotta: #C2410C;
      --color-terracotta-light: #EA580C;
      --color-olive: #4D7C0F;
      --color-olive-light: #65A30D;
      --color-ochre: #A16207;
      --color-sand: #E7E5E4;
      --color-sand-dark: #D6D3D1;

      font-family: 'Source Sans 3', -apple-system, sans-serif;
      background: var(--color-cream);
      color: var(--color-charcoal);
    }

    .font-editorial {
      font-family: 'Fraunces', Georgia, serif;
    }

    /* Paper texture overlay */
    .paper-texture::before {
      content: '';
      position: absolute;
      inset: 0;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
      opacity: 0.03;
      pointer-events: none;
      mix-blend-mode: multiply;
    }

    /* Hand-drawn underline effect */
    .hand-underline {
      position: relative;
      display: inline;
    }
    .hand-underline::after {
      content: '';
      position: absolute;
      left: -4px;
      right: -4px;
      bottom: -2px;
      height: 8px;
      background: url("data:image/svg+xml,%3Csvg width='100' height='8' viewBox='0 0 100 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 5C20 2 30 6 50 4C70 2 80 6 100 3' stroke='%23C2410C' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E") repeat-x;
      background-size: 100px 8px;
    }

    /* Animations */
    @keyframes rise-up {
      from { opacity: 0; transform: translateY(40px); }
      to { opacity: 1; transform: translateY(0); }
    }

    @keyframes fade-in {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .animate-rise {
      animation: rise-up 0.8s cubic-bezier(0.22, 1, 0.36, 1) forwards;
    }

    .animate-fade {
      animation: fade-in 0.6s ease-out forwards;
    }

    .delay-1 { animation-delay: 0.1s; }
    .delay-2 { animation-delay: 0.2s; }
    .delay-3 { animation-delay: 0.3s; }
    .delay-4 { animation-delay: 0.4s; }
    .delay-5 { animation-delay: 0.5s; }

    /* Organic shapes */
    .organic-shape {
      border-radius: 60% 40% 50% 50% / 50% 60% 40% 50%;
    }
  `}</style>
);

// ============================================
// STICKY MOBILE CTA BAR
// ============================================
const StickyMobileCTA: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  // Use Intersection Observer for reliable scroll detection
  useEffect(() => {
    // Find trigger elements in the DOM
    const heroTrigger = document.getElementById('hero-end-trigger');
    const bottomTrigger = document.getElementById('join');

    if (!heroTrigger) return;

    // Observer for when to show the CTA (after hero section)
    const showObserver = new IntersectionObserver(
      ([entry]) => {
        // Show CTA when hero trigger leaves viewport (scrolled past hero)
        if (!entry.isIntersecting) {
          setIsVisible(true);
        } else {
          setIsVisible(false);
        }
      },
      { threshold: 0, rootMargin: '-100px 0px 0px 0px' }
    );

    // Observer for when to hide the CTA (near final CTA section)
    const hideObserver = new IntersectionObserver(
      ([entry]) => {
        // Hide when final CTA section is visible
        if (entry.isIntersecting) {
          setIsVisible(false);
        }
      },
      { threshold: 0.2 }
    );

    showObserver.observe(heroTrigger);
    if (bottomTrigger) hideObserver.observe(bottomTrigger);

    return () => {
      showObserver.disconnect();
      hideObserver.disconnect();
    };
  }, []);

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 md:hidden transition-all duration-300 ${
        isVisible
          ? 'translate-y-0 opacity-100'
          : 'translate-y-full opacity-0'
      }`}
      style={{
        background: 'rgba(250, 247, 242, 0.98)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderTop: '1px solid var(--color-sand)',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.08)',
      }}
    >
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex-1 min-w-0">
          <p
            className="font-editorial text-sm font-semibold truncate"
            style={{ color: 'var(--color-charcoal)' }}
          >
            Join the Movement
          </p>
          <p
            className="text-xs truncate"
            style={{ color: 'var(--color-charcoal-muted)' }}
          >
            Be part of community-driven change
          </p>
        </div>
        <a
          href="/sign-up"
          className="shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white transition-all active:scale-95"
          style={{
            background: 'var(--color-terracotta)',
            boxShadow: '0 4px 12px -2px rgba(194, 65, 12, 0.4)',
          }}
        >
          Get Started
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </a>
      </div>
      {/* Safe area for iOS home indicator */}
      <div style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} />
    </div>
  );
};

// ============================================
// SCROLL ANIMATION HOOK
// ============================================
const useIntersectionObserver = (threshold = 0.1) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const options = useMemo(() => ({ threshold, rootMargin: '0px 0px -50px 0px' }), [threshold]);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, options);

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [options]);

  return { ref, isVisible };
};

// ============================================
// NAVIGATION
// ============================================
const Navigation: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const [moreOpen, setMoreOpen] = useState(false);

  const mainExploreLinks = [
    { label: 'Browse Issues', href: '/issues' },
    { label: 'Browse Solutions', href: '/solutions' },
    { label: 'Action Groups', href: '/action-groups' },
  ];

  const secondaryExploreLinks = [
    { label: 'Motivational Content', href: '/motivationals' },
    { label: 'Handout Sheets', href: '/handouts' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || mobileMenuOpen ? 'py-3 shadow-sm' : 'py-5'
      }`}
      style={{
        background: scrolled || mobileMenuOpen ? 'rgba(250,247,242,0.98)' : 'transparent',
        backdropFilter: scrolled || mobileMenuOpen ? 'blur(10px)' : 'none'
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <EditorialLogo />

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            {[
              { label: 'How It Works', id: 'how-it-works' },
              { label: 'Our Approach', id: 'approach' },
              { label: 'For Changemakers', id: 'changemakers' },
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-sm font-medium transition-colors hover:opacity-70"
                style={{ color: 'var(--color-charcoal-soft)' }}
              >
                {item.label}
              </button>
            ))}

            {/* Explore Dropdown */}
            <div className="relative">
              <button
                onClick={() => setExploreOpen(!exploreOpen)}
                onBlur={() => setTimeout(() => setExploreOpen(false), 150)}
                className="text-sm font-medium transition-colors hover:opacity-70 flex items-center gap-1"
                style={{ color: 'var(--color-charcoal-soft)' }}
              >
                Explore
                <svg
                  className={`w-4 h-4 transition-transform ${exploreOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {exploreOpen && (
                <div
                  className="absolute top-full right-0 mt-2 py-2 w-56 rounded-xl shadow-lg border"
                  style={{
                    background: 'var(--color-warm-white)',
                    borderColor: 'var(--color-sand)',
                  }}
                >
                  {/* Main links */}
                  {mainExploreLinks.map((link) => (
                    <a
                      key={link.href}
                      href={link.href}
                      className="block px-4 py-2.5 text-sm transition-colors hover:bg-black/5"
                      style={{ color: 'var(--color-charcoal-soft)' }}
                    >
                      {link.label}
                    </a>
                  ))}

                  {/* Divider */}
                  <div className="my-2 border-t" style={{ borderColor: 'var(--color-sand)' }} />

                  {/* More submenu */}
                  <div className="relative">
                    <button
                      onClick={(e) => { e.stopPropagation(); setMoreOpen(!moreOpen); }}
                      className="w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors hover:bg-black/5"
                      style={{ color: 'var(--color-charcoal-soft)' }}
                    >
                      <span>More</span>
                      <svg
                        className={`w-4 h-4 transition-transform ${moreOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* More dropdown */}
                    {moreOpen && (
                      <div className="pl-4 pb-2">
                        {secondaryExploreLinks.map((link) => (
                          <a
                            key={link.href}
                            href={link.href}
                            className="block px-4 py-2 text-sm transition-colors hover:bg-black/5 opacity-80"
                            style={{ color: 'var(--color-charcoal-soft)' }}
                          >
                            {link.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <a
              href="/sign-up"
              className="text-sm font-semibold px-5 py-2.5 rounded-full transition-all hover:scale-105"
              style={{ background: 'var(--color-charcoal)', color: 'var(--color-cream)' }}
            >
              Join the Movement
            </a>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t pt-4 space-y-4" style={{ borderColor: 'var(--color-sand)' }}>
            <button onClick={() => scrollToSection('how-it-works')} className="block text-base">How It Works</button>
            <button onClick={() => scrollToSection('approach')} className="block text-base">Our Approach</button>
            <button onClick={() => scrollToSection('changemakers')} className="block text-base">For Changemakers</button>

            {/* Mobile Explore Section */}
            <div className="pt-2 border-t" style={{ borderColor: 'var(--color-sand)' }}>
              <p className="text-xs uppercase tracking-wider mb-2 opacity-50">Explore Platform</p>
              {mainExploreLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="block py-2 text-base"
                  style={{ color: 'var(--color-charcoal-soft)' }}
                >
                  {link.label}
                </a>
              ))}

              {/* More section */}
              <p className="text-xs uppercase tracking-wider mb-2 mt-4 opacity-50">More</p>
              {secondaryExploreLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="block py-2 text-sm opacity-80"
                  style={{ color: 'var(--color-charcoal-soft)' }}
                >
                  {link.label}
                </a>
              ))}
            </div>

            <a href="/sign-up" className="inline-block px-5 py-2.5 rounded-full text-sm font-semibold mt-2" style={{ background: 'var(--color-charcoal)', color: 'var(--color-cream)' }}>
              Join the Movement
            </a>
          </div>
        )}
      </div>
    </nav>
  );
};

// ============================================
// HERO SECTION
// ============================================
const HeroSection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section className="relative min-h-screen pt-24 pb-16 paper-texture overflow-hidden">
      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: 'var(--color-terracotta)' }} />

      {/* Decorative shapes */}
      <div className="absolute top-20 right-0 w-64 h-64 organic-shape opacity-[0.04]" style={{ background: 'var(--color-terracotta)' }} />
      <div className="absolute bottom-40 left-0 w-96 h-96 organic-shape opacity-[0.03]" style={{ background: 'var(--color-olive)' }} />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 min-h-[calc(100vh-6rem)] flex flex-col">
        <div className="flex-1 grid lg:grid-cols-12 gap-8 lg:gap-12 items-center py-12">
          {/* Left Column */}
          <div className="lg:col-span-7">
            {/* Eyebrow */}
            <div className={`mb-6 ${isVisible ? 'animate-rise delay-1' : 'opacity-0'}`}>
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase" style={{ color: 'var(--color-olive)' }}>
                <span className="w-8 h-px" style={{ background: 'var(--color-olive)' }} />
                Decentralized Philanthropy
              </span>
            </div>

            {/* Headline */}
            <h1 className={`font-editorial leading-[0.95] mb-8 ${isVisible ? 'animate-rise delay-2' : 'opacity-0'}`}>
              <span className="block text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light">
                Philanthropy shouldn't
              </span>
              <span className="block text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light">
                flow <span className="italic font-normal" style={{ color: 'var(--color-charcoal-soft)' }}>top-down.</span>
              </span>
              <span className="block text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold mt-2">
                It should{' '}
                <span className="hand-underline" style={{ color: 'var(--color-terracotta)' }}>rise</span>
              </span>
              <span className="block text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-semibold">
                from communities.
              </span>
            </h1>

            {/* Subheadline */}
            <p className={`text-lg sm:text-xl max-w-xl leading-relaxed mb-10 ${isVisible ? 'animate-rise delay-3' : 'opacity-0'}`} style={{ color: 'var(--color-charcoal-soft)' }}>
              A platform where local knowledge meets global action. Identify issues in your community,
              propose solutions, and join forces with others to create real change.
            </p>

            {/* CTAs */}
            <div className={`flex flex-wrap items-center gap-4 ${isVisible ? 'animate-rise delay-4' : 'opacity-0'}`}>
              <a
                href="/sign-up"
                className="group inline-flex items-center gap-3 px-7 py-4 rounded-full font-semibold transition-all hover:scale-105 hover:shadow-lg"
                style={{ background: 'var(--color-terracotta)', color: 'var(--color-warm-white)' }}
              >
                Start Making Change
                <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
              <a
                href="/issues"
                className="group inline-flex items-center gap-2 px-6 py-4 rounded-full font-semibold border-2 transition-all hover:scale-105"
                style={{ borderColor: 'var(--color-charcoal)', color: 'var(--color-charcoal)' }}
              >
                Explore the Platform
                <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
              <a
                href="#how-it-works"
                className="inline-flex items-center gap-2 px-6 py-4 font-medium transition-colors hover:opacity-70"
              >
                See How It Works
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </a>
            </div>
          </div>

          {/* Right Column - Visual */}
          <div className={`lg:col-span-5 relative ${isVisible ? 'animate-rise delay-4' : 'opacity-0'}`}>
            <div className="relative">
              {/* Video with custom cover */}
              <div className="relative aspect-video rounded-sm overflow-hidden shadow-xl" style={{ background: 'var(--color-charcoal)' }}>
                {videoPlaying ? (
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src="https://www.youtube.com/embed/-oQP9JVDUNg?rel=0&modestbranding=1&autoplay=1"
                    title="WEattitude.org - Decentralized Philanthropy"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <button
                    onClick={() => setVideoPlaying(true)}
                    className="absolute inset-0 w-full h-full group cursor-pointer"
                    aria-label="Play video"
                  >
                    {/* Gradient background */}
                    <div
                      className="absolute inset-0"
                      style={{
                        background: 'linear-gradient(135deg, var(--color-charcoal) 0%, #2D2926 50%, var(--color-charcoal) 100%)',
                      }}
                    />

                    {/* Decorative pattern */}
                    <div className="absolute inset-0 opacity-10">
                      <div className="absolute top-4 left-4 w-24 h-24 border-2 border-current rounded-full" style={{ color: 'var(--color-terracotta)' }} />
                      <div className="absolute bottom-8 right-8 w-32 h-32 border border-current" style={{ color: 'var(--color-sand)' }} />
                      <div className="absolute top-1/2 left-1/4 w-16 h-16 border border-current transform -translate-y-1/2 rotate-45" style={{ color: 'var(--color-olive)' }} />
                    </div>

                    {/* Content */}
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                      {/* Logo text */}
                      <div className="mb-6 text-center">
                        <span className="text-2xl sm:text-3xl font-medium tracking-tight" style={{ fontFamily: "'Source Sans 3', sans-serif" }}>
                          WE<span className="italic" style={{ fontFamily: "'Fraunces', serif" }}>attitude</span><span className="opacity-50">.org</span>
                        </span>
                      </div>

                      {/* Play button */}
                      <div
                        className="w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                        style={{
                          background: 'var(--color-terracotta)',
                          boxShadow: '0 8px 30px rgba(194, 65, 12, 0.4)'
                        }}
                      >
                        <svg className="w-6 h-6 sm:w-8 sm:h-8 ml-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>

                      {/* Label */}
                      <p className="mt-4 text-sm font-medium tracking-wide opacity-70">
                        Watch the Demo
                      </p>
                    </div>
                  </button>
                )}
              </div>

              {/* Stats card */}
              <div className="absolute -bottom-6 -left-6 p-5 rounded-sm shadow-lg" style={{ background: 'var(--color-warm-white)', border: '1px solid var(--color-sand)' }}>
                <p className="font-editorial text-3xl font-semibold" style={{ color: 'var(--color-terracotta)' }}>12+</p>
                <p className="text-sm font-medium" style={{ color: 'var(--color-charcoal-soft)' }}>Years in development</p>
              </div>

              {/* Quote */}
              <div className="absolute -top-4 -right-4 max-w-[200px] p-4" style={{ background: 'var(--color-olive)', color: 'var(--color-warm-white)' }}>
                <p className="font-editorial text-sm italic leading-snug">"Change rises from those who live the reality"</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom trust bar */}
        <div className={`mt-auto pt-8 border-t ${isVisible ? 'animate-fade delay-5' : 'opacity-0'}`} style={{ borderColor: 'var(--color-sand)' }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-sm" style={{ color: 'var(--color-charcoal-soft)' }}>
              Built by communities, for communities. <span className="font-semibold">Open source</span> & <span className="font-semibold">transparent</span>.
            </p>
            <div className="flex items-center gap-6">
              {['Free Forever', 'Non-Profit', 'Community Governed'].map((badge) => (
                <span key={badge} className="text-xs font-medium tracking-wide uppercase opacity-60">{badge}</span>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Trigger for sticky mobile CTA */}
      <div id="hero-end-trigger" className="absolute bottom-0 left-0 right-0 h-1" aria-hidden="true" />
    </section>
  );
};

// ============================================
// PROBLEM/SOLUTION SECTION
// ============================================
const ProblemSolutionSection: React.FC = () => {
  const { ref, isVisible } = useIntersectionObserver();

  return (
    <section className="py-24 lg:py-32" style={{ background: 'var(--color-warm-white)' }}>
      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className={`max-w-3xl mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: 'var(--color-olive)' }}>
            The Problem We're Solving
          </span>
          <h2 className="font-editorial text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight mb-6">
            Traditional philanthropy is <span className="italic font-normal" style={{ color: 'var(--color-charcoal-soft)' }}>broken.</span>
          </h2>
          <p className="text-lg" style={{ color: 'var(--color-charcoal-soft)' }}>
            Decisions made in distant boardrooms rarely reflect the needs of communities on the ground.
            Resources flow top-down, filtered through bureaucracy, disconnected from local realities.
          </p>
        </div>

        {/* Three Columns */}
        <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
          {[
            {
              number: '01',
              title: 'The Challenge',
              description: 'Philanthropy is centralized, slow, and disconnected. Those with resources rarely understand local needs.',
              color: 'var(--color-terracotta)',
            },
            {
              number: '02',
              title: 'The Opportunity',
              description: 'Millions of people see problems daily and have ideas to fix them—but no platform to act collectively.',
              color: 'var(--color-ochre)',
            },
            {
              number: '03',
              title: 'The Solution',
              description: 'WEattitude.org decentralizes philanthropy, letting solutions rise from those closest to the problems.',
              color: 'var(--color-olive)',
            },
          ].map((item, index) => (
            <div
              key={item.number}
              className={`relative transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="mb-4">
                <span className="font-editorial text-5xl font-light" style={{ color: item.color, opacity: 0.3 }}>
                  {item.number}
                </span>
              </div>
              <h3 className="font-editorial text-xl font-semibold mb-3">{item.title}</h3>
              <p style={{ color: 'var(--color-charcoal-soft)' }}>{item.description}</p>
            </div>
          ))}
        </div>

        {/* Visual: Top-down vs Bottom-up */}
        <div className={`mt-20 flex flex-col sm:flex-row items-center justify-center gap-8 transition-all duration-700 delay-500 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="text-center opacity-40">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-2" style={{ background: 'var(--color-sand)' }}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
            </div>
            <span className="text-sm font-medium">Top-Down</span>
            <span className="block text-xs opacity-60">Old Way</span>
          </div>

          <div className="flex items-center gap-4">
            <div className="w-16 h-px" style={{ background: 'var(--color-sand-dark)' }} />
            <span className="text-sm" style={{ color: 'var(--color-charcoal-muted)' }}>vs</span>
            <div className="w-16 h-px" style={{ background: 'var(--color-olive)' }} />
          </div>

          <div className="text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-2 shadow-lg" style={{ background: 'var(--color-olive)', color: 'white' }}>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
            </div>
            <span className="text-sm font-semibold" style={{ color: 'var(--color-olive)' }}>Bottom-Up</span>
            <span className="block text-xs" style={{ color: 'var(--color-olive)', opacity: 0.7 }}>WEattitude Way</span>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// HOW IT WORKS SECTION
// ============================================
const HowItWorksSection: React.FC = () => {
  const { ref, isVisible } = useIntersectionObserver();

  const steps = [
    {
      number: '01',
      title: 'Identify',
      subtitle: 'See a problem? Report it.',
      description: 'Spot issues in your community or globally. Add context, location, and details. Your local knowledge matters.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      ),
      cta: { label: 'Report Issues', href: '/issues' },
    },
    {
      number: '02',
      title: 'Collaborate',
      subtitle: 'Propose solutions together.',
      description: 'Share ideas, discuss approaches, vote on the best solutions. Collective wisdom beats individual expertise.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      cta: { label: 'Recommend Solutions', href: '/solutions' },
    },
    {
      number: '03',
      title: 'Act',
      subtitle: 'Form groups. Take action.',
      description: 'Join action groups, contribute your skills, and work together to create measurable, lasting change.',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      cta: { label: 'Create/Join Action Groups', href: '/action-groups' },
    },
  ];

  return (
    <section id="how-it-works" className="py-24 lg:py-32 paper-texture relative overflow-hidden">
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-1/3 h-full opacity-[0.02]" style={{ background: 'var(--color-terracotta)' }} />

      <div ref={ref} className="relative max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className={`text-center max-w-2xl mx-auto mb-20 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: 'var(--color-terracotta)' }}>
            Simple Process
          </span>
          <h2 className="font-editorial text-3xl sm:text-4xl lg:text-5xl font-semibold mb-6">
            How It <span className="hand-underline" style={{ color: 'var(--color-terracotta)' }}>Works</span>
          </h2>
          <p className="text-lg" style={{ color: 'var(--color-charcoal-soft)' }}>
            Three steps to transform your ideas into real-world impact.
          </p>
        </div>

        {/* Steps */}
        <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
          {steps.map((step, index) => (
            <div
              key={step.number}
              className={`relative transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="p-8 rounded-sm h-full" style={{ background: 'var(--color-warm-white)', border: '1px solid var(--color-sand)' }}>
                {/* Number */}
                <div className="flex items-start justify-between mb-6">
                  <span className="font-editorial text-4xl font-light" style={{ color: 'var(--color-terracotta)', opacity: 0.4 }}>
                    {step.number}
                  </span>
                  <div style={{ color: 'var(--color-charcoal-soft)' }}>{step.icon}</div>
                </div>

                {/* Content */}
                <h3 className="font-editorial text-2xl font-semibold mb-2">{step.title}</h3>
                <p className="text-sm font-medium mb-4" style={{ color: 'var(--color-terracotta)' }}>{step.subtitle}</p>
                <p className="mb-6" style={{ color: 'var(--color-charcoal-soft)' }}>{step.description}</p>

                {/* CTA Button */}
                <a
                  href={step.cta.href}
                  className="inline-flex items-center gap-2 text-sm font-semibold transition-all hover:gap-3"
                  style={{ color: 'var(--color-terracotta)' }}
                >
                  {step.cta.label}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============================================
// FOR CHANGEMAKERS SECTION
// ============================================
const ForChangemakersSection: React.FC = () => {
  const { ref, isVisible } = useIntersectionObserver();

  const principles = [
    { title: 'Decentralized Model', description: 'Solutions from those closest to problems, not distant boardrooms.' },
    { title: 'Skills Matching', description: 'Connect expertise to issues for maximum impact.' },
    { title: 'Impact Measurement', description: 'Every solution scored for potential impact—data you can trust.' },
    { title: 'Radical Transparency', description: 'Open financials, open decisions, open source.' },
    { title: 'Succession Planning', description: 'Built to outlast any individual—sustainable by design.' },
  ];

  return (
    <section id="changemakers" className="py-24 lg:py-32" style={{ background: 'var(--color-charcoal)', color: 'var(--color-cream)' }}>
      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Content */}
          <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: 'var(--color-ochre)' }}>
              For Changemakers & Partners
            </span>
            <h2 className="font-editorial text-3xl sm:text-4xl lg:text-5xl font-semibold mb-6 leading-tight">
              What Makes Us <span className="italic font-normal" style={{ color: 'var(--color-sand)' }}>Different</span>
            </h2>
            <p className="text-lg mb-10 opacity-80">
              Built on principles that changemakers and partners can trust. Every decision traceable, every action accountable.
            </p>

            {/* CTA for changemakers */}
            <div className="p-6 rounded-sm" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <p className="font-medium mb-3">Ready to drive community-driven change?</p>
              <a
                href="#join"
                className="inline-flex items-center gap-2 text-sm font-semibold transition-colors"
                style={{ color: 'var(--color-ochre)' }}
              >
                Get in touch with our team
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>
            </div>
          </div>

          {/* Right: Principles */}
          <div className={`space-y-4 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            {principles.map((item, index) => (
              <div
                key={item.title}
                className="p-5 rounded-sm transition-colors"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                <h3 className="font-editorial text-lg font-semibold mb-1">{item.title}</h3>
                <p className="text-sm opacity-70">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// CAPABILITIES SECTION
// ============================================
const CapabilitiesSection: React.FC = () => {
  const { ref, isVisible } = useIntersectionObserver();

  const capabilities = [
    { title: 'AI-Powered Analysis', description: 'Every submission analyzed for clarity, relevance, and impact potential.' },
    { title: '11-Dimension Impact Rating', description: 'Issues rated across Education, Health, Climate, Security, Justice, and more.' },
    { title: 'Smart Skills Matching', description: 'Your expertise connected to issues where you can make the biggest difference.' },
    { title: 'Location-Aware Discovery', description: 'Find issues in your community or anywhere in the world.' },
    { title: 'Complete Audit Trail', description: 'Every action logged, every decision traceable. Nothing hidden.' },
  ];

  return (
    <section id="approach" className="py-24 lg:py-32" style={{ background: 'var(--color-warm-white)' }}>
      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className={`max-w-2xl mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: 'var(--color-olive)' }}>
            Platform Capabilities
          </span>
          <h2 className="font-editorial text-3xl sm:text-4xl lg:text-5xl font-semibold mb-6">
            Tools for <span className="hand-underline" style={{ color: 'var(--color-olive)' }}>Real</span> Impact
          </h2>
          <p className="text-lg" style={{ color: 'var(--color-charcoal-soft)' }}>
            We've built sophisticated features that make decentralized philanthropy actually work.
          </p>
        </div>

        {/* Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {capabilities.map((item, index) => (
            <div
              key={item.title}
              className={`p-6 rounded-sm transition-all duration-700 hover:shadow-md ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ background: 'var(--color-cream)', border: '1px solid var(--color-sand)', transitionDelay: `${index * 100}ms` }}
            >
              <h3 className="font-editorial text-lg font-semibold mb-2">{item.title}</h3>
              <p className="text-sm" style={{ color: 'var(--color-charcoal-soft)' }}>{item.description}</p>
            </div>
          ))}

          {/* CTA Card */}
          <div
            className={`p-6 rounded-sm transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ background: 'var(--color-olive)', color: 'white', transitionDelay: '500ms' }}
          >
            <h3 className="font-editorial text-lg font-semibold mb-2">Join the Founding Community</h3>
            <p className="text-sm opacity-90 mb-4">Be among the first changemakers. Help shape the platform.</p>
            <a href="/sign-up" className="inline-flex items-center gap-2 text-sm font-semibold">
              Get Early Access
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// TIMELINE SECTION
// ============================================
const TimelineSection: React.FC = () => {
  const { ref, isVisible } = useIntersectionObserver();

  const milestones = [
    { year: '2012', title: 'Concept Born', description: 'The idea of decentralized philanthropy takes shape.' },
    { year: '2015', title: 'First Prototype', description: 'Initial platform development begins.' },
    { year: '2019', title: 'Platform Redesign', description: 'Complete rebuild with modern architecture.' },
    { year: '2021', title: 'AI Integration', description: 'Advanced capabilities and analysis tools added.' },
    { year: '2024', title: 'Public Launch', description: 'Opening doors to the founding community.' },
    { year: 'Next', title: 'Scaling Worldwide', description: 'Growing the global network.', isNext: true },
  ];

  return (
    <section className="py-24 lg:py-32 paper-texture relative overflow-hidden">
      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-xs font-semibold tracking-widest uppercase mb-4" style={{ color: 'var(--color-charcoal-muted)' }}>
            Our Journey
          </span>
          <h2 className="font-editorial text-3xl sm:text-4xl lg:text-5xl font-semibold">
            Years in the Making
          </h2>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Center line */}
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px" style={{ background: 'var(--color-sand-dark)' }} />

          <div className="space-y-12">
            {milestones.map((milestone, index) => (
              <div
                key={milestone.year}
                className={`relative flex items-center transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                {/* Dot */}
                <div className="absolute left-4 md:left-1/2 w-3 h-3 -ml-1.5 rounded-full" style={{ background: milestone.isNext ? 'var(--color-terracotta)' : 'var(--color-olive)' }} />

                {/* Content */}
                <div className={`ml-12 md:ml-0 ${index % 2 === 0 ? 'md:pr-[calc(50%+2rem)] md:text-right' : 'md:pl-[calc(50%+2rem)]'}`}>
                  <div className="inline-block p-4 rounded-sm" style={{ background: 'var(--color-warm-white)', border: '1px solid var(--color-sand)' }}>
                    <span className="text-sm font-semibold" style={{ color: milestone.isNext ? 'var(--color-terracotta)' : 'var(--color-olive)' }}>{milestone.year}</span>
                    <h3 className="font-editorial text-lg font-semibold">{milestone.title}</h3>
                    <p className="text-sm" style={{ color: 'var(--color-charcoal-soft)' }}>{milestone.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// FINAL CTA SECTION
// ============================================
const FinalCTASection: React.FC = () => {
  const { ref, isVisible } = useIntersectionObserver();
  const [email, setEmail] = useState('');
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactStatus, setContactStatus] = useState<
    'idle' | 'submitting' | 'success' | 'error'
  >('idle');
  const [contactError, setContactError] = useState<string | null>(null);

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactStatus('submitting');
    setContactError(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id ?? null;

      const { error } = await supabase.from('user_messages').insert({
        name: contactForm.name.trim(),
        email: contactForm.email.trim().toLowerCase(),
        message: contactForm.message.trim(),
        user_id: userId,
        user_agent: navigator.userAgent,
        page_url: window.location.href,
      });

      if (error) {
        throw error;
      }

      setContactStatus('success');
      setContactForm({ name: '', email: '', message: '' });
    } catch (err: any) {
      setContactStatus('error');
      setContactError(
        err?.message || 'Something went wrong. Please try again.'
      );
    }
  };

  return (
    <section id="join" className="py-24 lg:py-32 relative overflow-hidden" style={{ background: 'var(--color-terracotta)' }}>
      {/* Texture overlay */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
      }} />

      <div ref={ref} className="relative max-w-6xl mx-auto px-6 lg:px-12" style={{ color: 'var(--color-warm-white)' }}>
        <div className={`transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Header */}
          <div className="text-center mb-12">
            <h2 className="font-editorial text-4xl sm:text-5xl lg:text-6xl font-semibold mb-6">
              Be a Founding<br />Changemaker
            </h2>
            <p className="text-xl opacity-90 max-w-2xl mx-auto">
              We're building something new. Join the first wave of people committed to decentralized, measurable social impact.
            </p>
          </div>

          {/* Two-column layout */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 max-w-5xl mx-auto">
            {/* Left: Join / Subscribe */}
            <div className="p-8 rounded-lg" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
              <h3 className="text-xl font-semibold mb-4">Join the Community</h3>
              <p className="opacity-80 mb-6">
                Create your free account and start making an impact today.
              </p>

              {/* CTA */}
              <a
                href="/sign-up"
                className="group inline-flex items-center gap-3 px-6 py-3 rounded-full font-semibold transition-all hover:scale-105 w-full justify-center mb-8"
                style={{ background: 'var(--color-warm-white)', color: 'var(--color-terracotta)' }}
              >
                Join the Founding Community
                <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </a>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t" style={{ borderColor: 'rgba(255,255,255,0.2)' }} />
                </div>
                <div className="relative flex justify-center">
                  <span className="px-4 text-sm opacity-60" style={{ background: 'transparent' }}>or subscribe for updates</span>
                </div>
              </div>

              {/* Newsletter */}
              <form className="flex gap-2" onSubmit={(e) => e.preventDefault()}>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-4 py-3 rounded-full text-sm placeholder:text-white/50"
                  style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                />
                <button
                  type="submit"
                  className="px-6 py-3 rounded-full text-sm font-semibold transition-all hover:bg-white/30"
                  style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)' }}
                >
                  Subscribe
                </button>
              </form>
            </div>

            {/* Right: Contact Form */}
            <div id="contact" className="p-8 rounded-lg scroll-mt-24" style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
              <h3 className="text-xl font-semibold mb-4">Send Us a Message</h3>
              <p className="opacity-80 mb-6">
                Have questions or ideas? We'd love to hear from you.
              </p>

              <form onSubmit={handleContactSubmit} className="space-y-4">
                <input
                  type="text"
                  placeholder="Your name"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                  required
                  disabled={contactStatus === 'submitting'}
                  className="w-full px-4 py-3 rounded-lg text-sm placeholder:text-white/50 disabled:opacity-60"
                  style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                />
                <input
                  type="email"
                  placeholder="Your email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                  required
                  disabled={contactStatus === 'submitting'}
                  className="w-full px-4 py-3 rounded-lg text-sm placeholder:text-white/50 disabled:opacity-60"
                  style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                />
                <textarea
                  placeholder="Your message"
                  value={contactForm.message}
                  onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                  required
                  rows={4}
                  disabled={contactStatus === 'submitting'}
                  className="w-full px-4 py-3 rounded-lg text-sm placeholder:text-white/50 resize-none disabled:opacity-60"
                  style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: 'white' }}
                />
                <button
                  type="submit"
                  disabled={contactStatus === 'submitting'}
                  className="w-full px-6 py-3 rounded-lg font-semibold transition-all hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{ background: 'var(--color-warm-white)', color: 'var(--color-terracotta)' }}
                >
                  {contactStatus === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>
                {contactStatus === 'success' && (
                  <p
                    className="text-sm text-center px-4 py-3 rounded-lg"
                    style={{ background: 'rgba(255,255,255,0.2)', color: 'white' }}
                  >
                    Thank you! Your message has been received. We'll get back to you soon.
                  </p>
                )}
                {contactStatus === 'error' && (
                  <p
                    className="text-sm text-center px-4 py-3 rounded-lg"
                    style={{ background: 'rgba(220,38,38,0.25)', color: 'white' }}
                  >
                    {contactError || 'Something went wrong. Please try again.'}
                  </p>
                )}
              </form>
            </div>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-12 text-sm opacity-70">
            {['Free forever', 'Open source', 'Your voice shapes the platform'].map((badge) => (
              <span key={badge} className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {badge}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// ============================================
// FOOTER
// ============================================
const Footer: React.FC = () => {
  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const mainExploreLinks = [
    { label: 'Browse Issues', href: '/issues' },
    { label: 'Browse Solutions', href: '/solutions' },
    { label: 'Action Groups', href: '/action-groups' },
  ];

  const secondaryExploreLinks = [
    { label: 'Motivational Content', href: '/motivationals' },
    { label: 'Handout Sheets', href: '/handouts' },
  ];

  return (
    <footer style={{ background: 'var(--color-charcoal)', color: 'var(--color-cream)' }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid md:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <a href="/" className="inline-flex items-baseline mb-4">
              <span className="font-editorial text-2xl font-semibold">WE</span>
              <span className="font-editorial text-2xl font-light italic opacity-70">attitude</span>
              <span className="text-xs opacity-40">.org</span>
            </a>
            <p className="opacity-60 max-w-sm mb-6">
              Built by communities, for communities. Decentralizing philanthropy, one solution at a time.
            </p>
            {/* Social */}
            <div className="flex gap-4">
              {['Twitter', 'LinkedIn', 'GitHub'].map((social) => (
                <a
                  key={social}
                  href={`#${social.toLowerCase()}`}
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-colors"
                  style={{ background: 'rgba(255,255,255,0.1)' }}
                >
                  <span className="sr-only">{social}</span>
                  <svg className="w-5 h-5 opacity-60" fill="currentColor" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                </a>
              ))}
            </div>
          </div>

          {/* About - Section Links */}
          <div>
            <h4 className="font-semibold mb-4">About</h4>
            <ul className="space-y-3 text-sm opacity-60">
              <li><button onClick={() => scrollToSection('how-it-works')} className="hover:opacity-100 transition-opacity">How It Works</button></li>
              <li><button onClick={() => scrollToSection('approach')} className="hover:opacity-100 transition-opacity">Our Approach</button></li>
              <li><button onClick={() => scrollToSection('changemakers')} className="hover:opacity-100 transition-opacity">For Changemakers</button></li>
              <li><button onClick={() => scrollToSection('join')} className="hover:opacity-100 transition-opacity">Join Us</button></li>
            </ul>
          </div>

          {/* Explore - App Links */}
          <div>
            <h4 className="font-semibold mb-4">Explore</h4>
            <ul className="space-y-3 text-sm opacity-60">
              {mainExploreLinks.map((link) => (
                <li key={link.href}>
                  <a href={link.href} className="hover:opacity-100 transition-opacity">{link.label}</a>
                </li>
              ))}
            </ul>
            <h5 className="font-medium mt-6 mb-3 text-sm opacity-70">More</h5>
            <ul className="space-y-3 text-sm opacity-50">
              {secondaryExploreLinks.map((link) => (
                <li key={link.href}>
                  <a href={link.href} className="hover:opacity-100 transition-opacity">{link.label}</a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-semibold mb-4">Legal</h4>
            <ul className="space-y-3 text-sm opacity-60">
              <li><a href="/privacy" className="hover:opacity-100 transition-opacity">Privacy Policy</a></li>
              <li><a href="/terms" className="hover:opacity-100 transition-opacity">Terms of Service</a></li>
              <li><button onClick={() => scrollToSection('contact')} className="hover:opacity-100 transition-opacity">Contact</button></li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm opacity-50">
          <p>&copy; 2025 WEattitude.org. All rights reserved.</p>
          <p>Decentralizing philanthropy for a better world.</p>
        </div>
      </div>
    </footer>
  );
};

// ============================================
// MAIN LANDING PAGE COMPONENT
// ============================================
const LandingPageRedesign: React.FC = () => {
  // Handle hash fragments (e.g. /#contact) — React SPAs don't scroll
  // automatically. Retry briefly because lazy sections / animations may
  // mount the target element a beat after the route renders.
  useEffect(() => {
    const hash = window.location.hash?.replace(/^#/, '');
    if (!hash) return;

    let attempts = 0;
    const maxAttempts = 20;
    const interval = window.setInterval(() => {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.clearInterval(interval);
      } else if (++attempts >= maxAttempts) {
        window.clearInterval(interval);
      }
    }, 100);

    return () => window.clearInterval(interval);
  }, []);

  return (
    <div className="landing-redesign">
      <SharedStyles />
      <Navigation />
      <HeroSection />
      <ProblemSolutionSection />
      <HowItWorksSection />
      <ForChangemakersSection />
      <CapabilitiesSection />
      <TimelineSection />
      <FinalCTASection />
      <Footer />
      <StickyMobileCTA />
    </div>
  );
};

export default LandingPageRedesign;
